import is from '@sindresorhus/is';
import { type ReactNode, useState } from 'react';

import { Button } from '../../core/button/Button';
import { Input } from '../../core/input/Input';
import type { ClarifyOption } from './types';

export type InChatClarifyCardProps = {
    readonly options: readonly ClarifyOption[];
    readonly onSelect: (value: string) => void;
    readonly disabled?: boolean;
    readonly otherPlaceholder?: string;
    readonly label?: string;
    readonly allowCustomInput?: boolean;
    readonly headerAction?: ReactNode;
    readonly headerContent?: ReactNode;
    readonly prompt?: ReactNode;
    readonly selectedValue?: string;
};

const letterAt = (index: number): string => String.fromCharCode(65 + index);

export function InChatClarifyCard({
    options,
    onSelect,
    disabled = false,
    otherPlaceholder = 'Other...',
    label = 'Choose an option',
    allowCustomInput = true,
    headerAction,
    headerContent,
    prompt,
    selectedValue,
}: InChatClarifyCardProps) {
    const [customText, setCustomText] = useState('');
    const hasSelectedOption = options.some((option) => option.headline === selectedValue);
    const customSelected = is.nonEmptyString(selectedValue) && !hasSelectedOption;
    const hasHeaderContent = !is.nullOrUndefined(headerContent);
    const hasHeaderAction = !is.nullOrUndefined(headerAction);
    const hasLabel = is.nonEmptyString(label);
    const hasPrompt = !is.nullOrUndefined(prompt);

    const submitCustom = (): void => {
        const value = customText.trim();
        if (is.emptyString(value)) return;
        onSelect(value);
        setCustomText('');
    };

    const customInputContent = (
        <>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-gray-200 text-xxs font-semibold text-black-400">
                {letterAt(options.length)}
            </span>
            <Input
                value={customText}
                onChange={(event) => setCustomText(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && customText.trim().length > 0) {
                        event.preventDefault();
                        submitCustom();
                    }
                }}
                placeholder={customSelected ? selectedValue : otherPlaceholder}
                disabled={disabled}
                className="!h-5 flex-1 !border-0 rounded-none !bg-transparent !p-0 !text-sm !font-medium !text-black-700 !shadow-none !outline-none placeholder:!font-medium placeholder:!text-black-400"
            />
        </>
    );

    return (
        <div className="flex w-full flex-col gap-s font-sans">
            {hasHeaderContent || hasLabel || hasHeaderAction ? (
                <div className="flex min-h-5 items-start justify-between gap-s px-xs">
                    {hasHeaderContent ? (
                        <div className="text-sm font-medium leading-snug text-black-800">{headerContent}</div>
                    ) : hasLabel ? (
                        <span className="text-xxs font-medium uppercase tracking-[0.08em] text-black-400">{label}</span>
                    ) : null}
                    {headerAction}
                </div>
            ) : null}
            {hasPrompt ? <div className="px-xs text-sm font-medium leading-snug text-black-800">{prompt}</div> : null}
            <div className="flex flex-col items-stretch gap-s">
                {options.map((option, index) => {
                    const recommended = option.recommended === true;
                    const selected = option.headline === selectedValue;
                    const primary = recommended && !selected;
                    return (
                        <Button
                            key={option.headline}
                            variant={selected ? 'secondary' : primary ? 'primary' : 'secondary'}
                            disabled={disabled}
                            isActive={selected}
                            onClick={() => onSelect(option.headline)}
                            className="group/clarify-option w-full !items-start !justify-start gap-s text-left"
                        >
                            <span
                                className={`mt-xxs flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-xxs font-semibold ${primary ? 'bg-black-700 text-white-200' : 'bg-gray-200 text-black-500'}`}
                            >
                                {letterAt(index)}
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col justify-center self-center">
                                <span
                                    className={`text-sm font-medium leading-snug ${primary ? 'text-white-200' : 'text-black-800'}`}
                                >
                                    {option.headline}
                                </span>
                                {is.nonEmptyString(option.subline) ? (
                                    <span
                                        className={`text-xs font-normal leading-snug ${primary ? 'text-gray-300' : 'text-black-500'}`}
                                    >
                                        {option.subline}
                                    </span>
                                ) : null}
                            </span>
                        </Button>
                    );
                })}
                {allowCustomInput ? (
                    <Button
                        as="div"
                        variant="secondary"
                        isActive={customSelected}
                        className="w-full !items-center !justify-start gap-s text-left"
                    >
                        {customInputContent}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
